import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { QuestionnaireCategory } from 'src/app/models/questionnaireCategory.model';
import { QuestionnaireStore } from 'src/app/store/questionnaire-store';
import { GlobalStore } from 'src/app/store/global-store';
import { combineLatest, debounceTime, filter, map, startWith, take, tap } from 'rxjs';
import { MiniAddLogoComponent } from '../question-board/mini-add-logo/mini-add-logo.component'
import { LogoDisplayComponent } from '../question-board/logo-display/logo-display.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { SvgIconComponent } from 'angular-svg-icon';
import { TostesStore } from 'src/app/store/tostes-store';
import { TostPositionEnum, TostTypeEnum } from 'src/app/shared/models/tostes.model';
import { DragStore } from 'src/app/store/drag-store';
import { Validators } from 'ngx-editor';
import { AnswersStore } from 'src/app/store/answers-store';

@Component({
    selector: 'que-definitions-in-board',
    imports: [CommonModule, TranslateDirective, TranslatePipe, ReactiveFormsModule, MiniAddLogoComponent, LogoDisplayComponent, OverlayModule],
    templateUrl: './definitions-in-board.component.html',
    styleUrls: ['./definitions-in-board.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefinitionsInBoardComponent implements OnInit {
  @Input() isEdit = false;

  readonly positonArea = [{
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
  }] as ConnectedPosition[];

  readonly positonAreaEdit = [{
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'top',
  }] as ConnectedPosition[];

  colorForm: any;
  isMenuOpen = false;
  isColorsPanelOpen = false;
  #questionnaireStore = inject(QuestionnaireStore);
  #dragStore = inject(DragStore);
  #globalStore = inject(GlobalStore);
  #answersStore = inject(AnswersStore);
  #tostesStore = inject(TostesStore);
  #fb = inject(FormBuilder);
  name = new FormControl<string>('', [Validators.maxLength(50)]);
  categoryId = new FormControl<number>(0);
  categories$ = this.#globalStore.allCategories$;
  name$ = this.#questionnaireStore.name$
  lang$ = this.#globalStore.language$;
  openEditNow = false;
  secondaryColor$ = this.#questionnaireStore.secondaryColor$;
  mainColor$ = this.#questionnaireStore.mainColor$;
  coverName$ = toSignal(this.#questionnaireStore.binding$.pipe(map(binding => binding.name)));
  coverImage$ = toSignal(this.#questionnaireStore.binding$.pipe(map(binding => binding.base64)));
  isBindingOutOfRange = false;
  previousName = "";
  showMaxLengthError = false;

  isAfterInitCategory = false;
  currentCategory$ = toSignal(combineLatest([this.categories$, this.categoryId.valueChanges])
    .pipe(
      map(
        ([categories, categoryId]) => categories.find(c => c.id == (categoryId ?? 6))
      )
      , startWith(
        {
          id: 0, name: "לא נבחרה תיקיה", enName: "No folder selected"
        }
      )));

  ngOnInit(): void {
    this.#questionnaireStore.name$.pipe(take(1)).subscribe(
      n => {
        this.previousName = n ?? '';
        this.name.setValue(n ?? '', { emitEvent: false })
      }
    )
    this.name.valueChanges.pipe(
      tap(x => this.openEditNow = true),
      tap(n => {
        if ((n?.length ?? 0) > 70) {
          this.name.setValue(this.previousName, { emitEvent: false });
          this.showMaxLengthError = true;
        }
        else {
          this.showMaxLengthError = false;
          this.previousName = n ?? '';
          this.#questionnaireStore.updateAfterInitDefinitions();
          this.#questionnaireStore.updatDefinitionsName(n ?? undefined)
        }
      }),
      debounceTime(500))
      .subscribe(x => {
        this.showMaxLengthError = false;
        this.openEditNow = false
      });
    this.#questionnaireStore.category$.pipe(filter(x => x != undefined), take(1)).subscribe(
      c => {
        this.categoryId.setValue(c!.id, { emitEvent: true });
        setTimeout(() => {
          this.isAfterInitCategory = true;
        }, 10);
      }
    )
    this.categoryId.valueChanges.pipe(filter(c => c != null)).subscribe(
      id => {
        if (this.isAfterInitCategory) {
          this.#questionnaireStore.updateAfterInitDefinitions();
        }
        this.#questionnaireStore.updatDefinitionsCategory(this.categories$.pipe(map(x => x.find(c => c.id == id))));
      }
    )

    this.colorForm = this.#fb.group({
      mainColor: [''],
      secondaryColor: [''],
      backColor: ['']
    });
    this.colorForm.controls.mainColor.valueChanges
      .subscribe((color: string) =>{
        this.#questionnaireStore.updateAfterInitDefinitions();
        this.#questionnaireStore.updateMainColor(color)});

    this.colorForm.controls.secondaryColor.valueChanges
      .subscribe((color: string) => {
        this.#questionnaireStore.updateAfterInitDefinitions();
        this.#questionnaireStore.updateSecondaryColor(color);
      });
    this.colorForm.controls.backColor.valueChanges.subscribe((color: string) => {
      this.#questionnaireStore.updateAfterInitDefinitions();
      this.#questionnaireStore.updateBackColor(color);
    });

    this.#questionnaireStore.mainColor$.pipe(take(1)).subscribe(mainColor => {
      this.colorForm.controls.mainColor.setValue(mainColor, { emitEvent: false });
    });

    this.#questionnaireStore.secondaryColor$.pipe(take(1)).subscribe(secondaryColor => {
      this.colorForm.controls.secondaryColor.setValue(secondaryColor, { emitEvent: false });
    });
    this.#questionnaireStore.backColor$.pipe(take(1)).subscribe(backColor => {
      this.colorForm.controls.backColor.setValue(backColor, { emitEvent: false });
    });
  }
  blur() {
    this.openEditNow = false;
  }
  updateSelection(id: number) {
    this.categoryId.setValue(id);
    this.isMenuOpen = false;
  }
  cleanTitle() {
    this.name.setValue('');
  }
  reset() {
    //values to configuration
    const baseColorMain = "#1390CF";
    const baseColorSeconary = "#D1EFFF";
    const baseColorBack = "#F0F3FA";
    this.colorForm.setValue({
      mainColor: baseColorMain,
      secondaryColor: baseColorSeconary,
      backColor: baseColorBack
    })
    this.clearFile();
  }
  updateSelectedColorSecondaly(event: Event) {
    const colorInput = event.target as HTMLInputElement;
    this.colorForm.control.secondaryColor.setValue(colorInput.value);
  }
  onFileSelected(event: any) {
    this.isBindingOutOfRange = false;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const fileName = input.files[0].name;
      const file = event.target.files[0]
      const maxSize = 1001 * 1024;
      if (file.size > maxSize) {
        // File size exceeds the limit
        this.#tostesStore.addTost({ guid: this.#tostesStore.generateGuid(), message: { en: "File size exceeds the maximum allowed size (1000KB)", he: "גודל הקובץ חורג מהגודל המרבי המותר (1000KB)" }, position: TostPositionEnum.BottomLeft, seconds: 5, type: TostTypeEnum.ERROR })
        this.isBindingOutOfRange = true;
        // this.#questionnaireStore.updateBinding(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        var base64 = reader.result;
        this.#questionnaireStore.updateAfterInitDefinitions();
        this.#questionnaireStore.updateBinding({ base64: base64 as string, name: fileName ?? "" });
      };
    }
  }

  clearFile(): void {
    this.#questionnaireStore.updateAfterInitDefinitions();
    this.#questionnaireStore.updateBinding(null);
  }
}
